import type {MutationTree} from "vuex";
import type {SolutionGuide} from "../../../../../_types/solutionguide";
import type {Productfinder} from "../../../../../_types/productfinder";
import {State, STEP} from "./solutionGuideStore";
import {isIndividualProduct, isIndividualSelected, isProductAvailable} from "./solutionGuideGetters";
import Vue, {set} from "vue";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function showPropertiesStep(state: State) {
    return true // As of PFLQS-1301 we always show the step.  old: state.component?.additionalPropertyIds.length
}

function showDecorStep(state: State) {
    if (state.result) {
        return state.result && !isIndividualProduct(state.result) && state.result.decors.length > 0
    }

    if (state.segment && state.application && state.component) {
        return state.component.productIds.map(
            productId => state.segment?.productMap[productId]).filter(
            product => {
                return product && !isIndividualProduct(product) && isProductAvailable(state, product)
            }).length > 0
    }

    return false;
}

const mutateResult = (state: State) => {
    if (state.segment && state.application && state.component && state.decor) {
        // Select first product that is available (that means it's matching all properties if properties are set)

        const products = state.component.productIds.map(productId => state.segment?.productMap[productId])

        const availableProducts = products.filter(product => {
            if (product) {
                return isProductAvailable(state, product);
            }
            return false
        });

        const productsWithDecor = availableProducts.filter(product => {
            if (product && state.decor) {
                return product.decors.indexOf("P" + state.decor.pim_id) !== -1
            }

            return false
        });

        if (productsWithDecor.length > 0) {
            state.result = productsWithDecor[0] || null
        } else {
            state.result = null
        }

        // Fall back to the first product of the component if nothing was matched
        if (state.result) {
            set(state.selectedDecors, state.component.name + '#' + state.result.pim_id, state.decor)
        }

        if (state.result === null) {
            state.result = state.segment.productMap[state.component.productIds[0]]
        }
    }

    if (isIndividualSelected(state) && state.segment && state.application && state.component) {
        const products = state.component.productIds.map(productId => state.segment?.productMap[productId])

        const availableProducts = products.filter(product => {
            if (product) {
                return isProductAvailable(state, product);
            }
            return false
        });

        if (availableProducts.length > 0) {
            state.result = availableProducts[0] || null
        } else {
            state.result = null
        }

        // Fall back to the first product of the component if nothing was matched
        if (state.result) {
            set(state.selectedDecors, state.component.name + '#' + state.result.pim_id, state.decor)
        }

        if (state.result === null) {
            state.result = state.segment.productMap[state.component.productIds[0]]
        }
    }
}

const mutateNext = (state: State) => {
    if (state.activeStep === STEP.Application) {
        if (state.application) {
            state.activeStep = STEP.Component
        }
        return;
    }
    if (state.activeStep === STEP.Component) {
        if (state.component) {
            state.activeStep = STEP.Property
        }
        // Skip if no selectable properties ... ?
        // i.e. skip the return and go to the next if
        if (state.activeStep === STEP.Property && showPropertiesStep(state)) {
            return;
        }
    }
    if (state.activeStep === STEP.Property) {
        state.activeStep = STEP.Decor
        // reset decor selection to be safe
        state.selectedDecors = {}
        state.decor = null;
        // Skip Decor directly to results if individual Ding is ausgewählt  ----- NEIN. Skip if product has no decor selection.
        // i.e. skip the return and go to the next if
        if (state.activeStep === STEP.Decor && showDecorStep(state)) {
            return;
        } else {
            mutateResult(state)
        }
    }
    if (state.activeStep === STEP.Decor) {
        // if (!showDecorStep(state)) {
        state.activeStep = STEP.Result
        mutateResult(state)
        // }
        return;
    }
}

const mutateBack = (state: State) => {
    if (state.activeStep === STEP.Result) {
        state.selectedDecors = {}
        state.result = null
        state.decor = null
        state.activeStep = STEP.Decor
        // Skip Decor directly to results if individual Ding is ausgewählt  ----- NEIN. Skip if product has no decor selection.
        // i.e. skip the return and go to the next if
        if (showDecorStep(state)) {
            return
        }
    }
    if (state.activeStep === STEP.Decor) {
        state.result = null
        state.activeStep = STEP.Property
        // Skip Decor directly to results if individual Ding is ausgewählt  ----- NEIN. Skip if product has no decor selection.
        // i.e. skip the return and go to the next if
        if (showPropertiesStep(state)) {
            return;
        }
    }
    if (state.activeStep === STEP.Property) {
        state.properties = [] // Reset only after going back before properties. This saves the selection when coming back from decor or result
        state.component = null
        state.activeStep = STEP.Component
        return
    }
    if (state.activeStep === STEP.Component) {
        state.application = null
        state.activeStep = STEP.Application
        return
    }
}

export default <MutationTree<State>>{
    init(state, payload) {
        state.segment = payload.segmentData
        state.ctalink = payload.ctalink
    },
    _updateViewport(state, payload) {
        state.activeBreakpoint = payload
    },
    selectApplication(state, payload : SolutionGuide.Application) {
        state.application = payload
        mutateNext(state)
    },
    selectComponent(state, payload : SolutionGuide.Component) {
        state.component = payload
        mutateNext(state)
    },
    toggleProperty(state, payload : string) {
        const index = state.properties.indexOf(payload)
        if (index < 0) {
            state.properties.push(payload)
        }
        else {
            state.properties.splice(index, 1)
        }
    },
    selectDecor(state, payload : { key: string, decor: Productfinder.Product|null }) {
        if (payload.decor === null) {
            if (state.selectedDecors[payload.key]) {
                Vue.delete(state.selectedDecors, payload.key)
            }
        }
        else {
            set(state.selectedDecors, payload.key, payload.decor)
        }
    },
    chooseDecor(state, payload: { decor: Productfinder.Product|null}) {
        if (payload.decor === null) {
            state.decor = null;
        } else {
            state.decor = payload.decor
        }
    },
    next(state) {
        mutateNext(state)
    },
    back(state) {
        mutateBack(state)
    }
}
